/* eslint-disable @reatom/unit-naming-rule */
import { isAbortError } from '@reatom/framework'

import { Data, Result } from '@blade/std-lib'
import { UnknownError, NotFoundError } from '@root/shared'
import {
  reatomFactoryCache,
  reatomResource,
  worthAskingAgain,
} from '@root/shared/lib'

import { CurrencyModel } from '../model'

export class IconProcessing extends Data.TaggedClass('IconProcessing') {}

const reatomCurrencyIcon_ = ({ currency }: { currency: CurrencyModel }) => {
  const stateAtom = reatomResource<
    UnknownError | NotFoundError | IconProcessing,
    undefined | string
  >(
    async (ctx, { reason }) => {
      const iconSrc = ctx.spy(currency.icon)
      if (!iconSrc) {
        return Result.succeed(undefined)
      }
      const isNative = CurrencyModel.isNativeOrWrapped(currency.network)(
        currency,
      )
      if (isNative) {
        return Result.succeed(undefined)
      }
      const status = ctx.spy(currency.iconStatus)
      if (status === 'processing') {
        return Result.fail(new IconProcessing())
      }

      const moderationReason = ctx.spy(currency.moderationReason)
      if (
        moderationReason === 'fetch_failed' ||
        moderationReason === 'no_onchain_logo'
      ) {
        return Result.fail(new NotFoundError())
      }
      try {
        const resolvedIconSrc =
          reason === 'retry' ? `${iconSrc}?retry=${Date.now()}` : iconSrc
        const response = await ctx.schedule(() => fetch(resolvedIconSrc))
        if (!response.ok) {
          if (response.status === 404) {
            return Result.fail(new NotFoundError())
          }

          return Result.fail(UnknownError.fromUnknown(response.status))
        }
        return Result.succeed(iconSrc)
      } catch (error) {
        if (isAbortError(error)) {
          throw error
        }
        return Result.fail(UnknownError.fromUnknown(error))
      }
    },
    {
      retry: {
        enabled: true,
        strategy: 'exponential',
        // A missing logo stays missing; see `worthAskingAgain`.
        retryOn: worthAskingAgain,
      },
    },
  )

  return stateAtom
}

export const reatomCurrencyIcon = reatomFactoryCache({
  factory: reatomCurrencyIcon_,
  keyFn: ({ currency }) => currency.address + currency.network,
  clearOnDisconnect: false,
})
